import { useMemo, useState, useEffect, useRef } from "react";
import { Calendar } from "primereact/calendar";

/* row + calendar styling to match PriceSlider */
const styles = `
.mg-release-date-box {
  width: 100%;
  min-height: 36px;
  background: #050505;
  border: 1px solid #444;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  color: rgba(187,187,187,0.75);
  font-weight: normal;
  box-sizing: border-box;
  margin-bottom: 10px;
}

/* Row + label sizing (same as Price) */
.mg-date-row { display:grid; grid-template-columns:56px 1fr; column-gap:8px; align-items:center; width:100%; margin-top:10px; }
.mg-date-label { color:rgba(187,187,187,0.75); white-space:nowrap; font-weight:normal; }

/* -- FLUSH calendar field (no double border) -- */
.mg-cal .p-calendar { width:100%; background:transparent !important; border:0 !important; box-shadow:none !important; }

/* Input: left side only */
.mg-cal .p-inputtext {
  width:100%;
  height:36px !important;
  background:#050505 !important;
  color:rgba(187,187,187,0.75) !important;
  border:1px solid #444 !important;
  border-right:0 !important;                       /* remove middle seam */
  border-radius:8px 0 0 8px !important;            /* only left corners */
  padding:6px 10px !important;
  box-shadow:none !important;
  outline:none !important;
}

/* Button tab: right side only */
.mg-cal .p-calendar .p-datepicker-trigger,
.mg-cal .p-calendar .p-datepicker-trigger.p-button {
  height:36px !important;
  width:38px !important;
  background:#050505 !important;
  color:rgba(187,187,187,0.75) !important;
  border:1px solid #444 !important;
  border-left:0 !important;                        /* joins input */
  border-radius:0 8px 8px 0 !important;            /* only right corners */
  box-shadow:none !important;
  outline:none !important;
}

/* Keep it clean on hover/focus */
.mg-cal .p-calendar .p-datepicker-trigger:enabled:hover,
.mg-cal .p-calendar .p-datepicker-trigger:enabled:focus,
.mg-cal .p-calendar .p-datepicker-trigger.p-focus,
.mg-cal .p-inputtext:enabled:focus {
  background:#111 !important;
  border-color:#666 !important;
  box-shadow:none !important;
  outline:none !important;
}

/* Nix any theme pseudo-frames that re-add a border */
.mg-cal .p-calendar .p-datepicker-trigger::before,
.mg-cal .p-calendar .p-datepicker-trigger::after { display:none !important; }

/* This popup is portaled to document.body, so it must out-rank the Steam
   filter popover (z-index 10000) itself or it renders invisibly behind it. */
.mg-steam-date-panel.p-datepicker { z-index: 10050 !important; }
`;

const parseDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;

  try {
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

const adjustDateHours = (date, isStart = true) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return null;

  const adjusted = new Date(parsedDate);
  if (isStart) adjusted.setHours(0, 0, 0, 0);
  else adjusted.setHours(23, 59, 59, 999);
  return adjusted;
};

// `forceClose` lets the parent tell us to dismiss any open calendar popup
// (PrimeReact portals it to document.body, so it doesn't close on its own
// when this filter panel itself is hidden/closed).
export default function DateSlider({ range, selected, onChange, onClear, forceClose, compact = false, headerContent = null }) {
  const minDate = useMemo(() => {
    const date = parseDate(range?.[0]);
    return date ? adjustDateHours(date, true) : null;
  }, [range?.[0]]);

  const maxDate = useMemo(() => {
    const date = parseDate(range?.[1]);
    return date ? adjustDateHours(date, false) : null;
  }, [range?.[1]]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    const start = parseDate(selected?.[0]);
    const end = parseDate(selected?.[1]);

    setStartDate(start ? adjustDateHours(start, true) : minDate);
    setEndDate(end ? adjustDateHours(end, false) : maxDate);
  }, [selected?.[0], selected?.[1], minDate, maxDate]);

  const startCalRef = useRef(null);
  const endCalRef = useRef(null);

  useEffect(() => {
    if (!forceClose) return;
    startCalRef.current?.hide();
    endCalRef.current?.hide();
  }, [forceClose]);

  const min = minDate ? +minDate : 0;
  const max = maxDate ? +maxDate : 0;

  const emit = (s, e) => {
    if (!minDate || !maxDate) return;
    if (+s === min && +e === max) onClear?.();
    else onChange?.([s, e]);
  };

  const changeStart = (val) => {
    if (!val) return;

    const s = adjustDateHours(val, true) || minDate;
    let e = endDate || maxDate;
    if (+s > +e) e = adjustDateHours(s, false);
    setStartDate(s);
    setEndDate(e);
    emit(s, e);
  };

  const changeEnd = (val) => {
    if (!val) return;

    const e = adjustDateHours(val, false) || maxDate;
    let s = startDate || minDate;
    if (+e < +s) s = adjustDateHours(e, true);
    setStartDate(s);
    setEndDate(e);
    emit(s, e);
  };

  const todayStart = () => {
    const t = adjustDateHours(new Date(), true);
    let e = endDate || maxDate;
    if (+t > +e) e = adjustDateHours(t, false);
    setStartDate(t);
    setEndDate(e);
    emit(t, e);
  };

  const todayEnd = () => {
    const t = adjustDateHours(new Date(), false);
    let s = startDate || minDate;
    if (+t < +s) s = adjustDateHours(t, true);
    setStartDate(s);
    setEndDate(t);
    emit(s, t);
  };

  const clearStart = () => {
    const s = minDate;
    let e = endDate || maxDate;
    if (+s > +e) e = maxDate;
    setStartDate(s);
    setEndDate(e);
    emit(s, e);
  };

  const clearEnd = () => {
    const e = maxDate;
    let s = startDate || minDate;
    if (+e < +s) s = minDate;
    setStartDate(s);
    setEndDate(e);
    emit(s, e);
  };

  const calendarProps = {
    selectionMode: "single",
    showIcon: true,
    showButtonBar: true,
    dateFormat: "mm/dd/yy",
    placeholder: "Select date",
    style: { width: "100%" },
    panelClassName: "mg-steam-date-panel",
  };

  return (
    <div className={compact ? "mg-release-date-compact" : undefined}>
      <style>{styles}</style>

      <div className="mg-release-date-box">Release Date</div>
      {headerContent}

      {/* Start */}
      <div className="mg-date-row mg-cal">
        {!compact && <span className="mg-date-label">Start</span>}
        <Calendar
          {...calendarProps}
          placeholder={compact ? "Start Date" : calendarProps.placeholder}
          aria-label="Start Date"
          formatDateTime={compact ? (value) => `Start Date: ${value.toLocaleDateString()}` : undefined}
          readOnlyInput={compact}
          ref={startCalRef}
          value={startDate}
          onChange={(e) => changeStart(e.value)}
          minDate={minDate}
          maxDate={endDate || maxDate}
          onTodayButtonClick={todayStart}
          onClearButtonClick={clearStart}
        />
      </div>

      {/* End */}
      <div className="mg-date-row mg-cal">
        {!compact && <span className="mg-date-label">End</span>}
        <Calendar
          {...calendarProps}
          placeholder={compact ? "End Date" : calendarProps.placeholder}
          aria-label="End Date"
          formatDateTime={compact ? (value) => `End Date: ${value.toLocaleDateString()}` : undefined}
          readOnlyInput={compact}
          ref={endCalRef}
          value={endDate}
          onChange={(e) => changeEnd(e.value)}
          minDate={startDate || minDate}
          maxDate={maxDate}
          onTodayButtonClick={todayEnd}
          onClearButtonClick={clearEnd}
        />
      </div>
    </div>
  );
}
